/**
 * Shared deferred sort state hook for mobile scouting lists.
 *
 * Manages a pending sort selection (field + direction) in a bottom sheet
 * and an active sort that is applied when the user presses "Apply sort".
 * @module features/scouting/hooks/useDeferredSort
 */

import { useCallback, useState } from "react";

/** Configuration for the hook */
interface UseDeferredSortOptions {
  /** Initial sort field (null = no sort) */
  readonly initialField?: string | null;
  /** Initial sort direction */
  readonly initialDir?: "asc" | "desc";
  /** Called after sort is applied with the active field */
  readonly onApplied?: (field: string | null, dir: "asc" | "desc") => void;
}

/** Return value of useDeferredSort */
interface DeferredSortState {
  /** Currently applied sort field */
  readonly activeSortField: string | null;
  /** Currently applied sort direction */
  readonly activeSortDir: "asc" | "desc";
  /**
   * Whether the user has applied a sort from the sheet at least once.
   *
   * `activeSortField` cannot answer this on its own: callers seed it with an
   * `initialField` (the Search tab seeds `"GPR"`), so "GPR is active" is
   * ambiguous between "the app's default" and "the user asked for GPR".
   * Consumers that must treat the two differently — e.g. a client-side
   * re-order that should only override the server's ordering on an explicit
   * request — read this flag rather than comparing `activeSortField` to the
   * initial value (SE-7581).
   *
   * Latches `true` on the first `handleApplySort` and never returns to
   * `false`: clearing the sort is itself a stated preference.
   */
  readonly hasExplicitSort: boolean;
  /** In-sheet pending sort field selection */
  readonly pendingSortField: string | null;
  /** In-sheet pending sort direction */
  readonly pendingSortDir: "asc" | "desc";
  /** Whether the sort sheet is visible */
  readonly isSortSheetOpen: boolean;
  /** Opens the sort sheet, seeding pending values from active */
  readonly handleSortPress: () => void;
  /** Closes the sort sheet without applying */
  readonly handleCloseSortSheet: () => void;
  /** Selects a sort field in the sheet */
  readonly handleSelectSort: (field: string) => void;
  /** Sets pending direction to ascending */
  readonly handleSelectAsc: () => void;
  /** Sets pending direction to descending */
  readonly handleSelectDesc: () => void;
  /** Applies pending sort to active and closes the sheet */
  readonly handleApplySort: () => void;
}

/**
 * Manages deferred sort state for mobile scouting sort sheets.
 *
 * The "pending" values are manipulated in the sort sheet UI. When the
 * user taps "Apply sort", they become the "active" values used by the
 * list query/sort logic.
 * @param options - Configuration options
 * @returns Deferred sort state and handlers
 */
function useDeferredSort(
  options: UseDeferredSortOptions = {}
): DeferredSortState {
  const { initialField = null, initialDir = "desc", onApplied } = options;

  const [activeSortField, setActiveSortField] = useState<string | null>(
    initialField
  );
  const [activeSortDir, setActiveSortDir] = useState<"asc" | "desc">(
    initialDir
  );
  const [pendingSortField, setPendingSortField] = useState<string | null>(
    initialField
  );
  const [pendingSortDir, setPendingSortDir] = useState<"asc" | "desc">(
    initialDir
  );
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);
  const [hasExplicitSort, setHasExplicitSort] = useState(false);

  const handleSortPress = useCallback(() => {
    setPendingSortField(activeSortField);
    setPendingSortDir(activeSortDir);
    setIsSortSheetOpen(true);
  }, [activeSortField, activeSortDir]);

  const handleCloseSortSheet = useCallback(() => {
    setIsSortSheetOpen(false);
  }, []);

  const handleSelectSort = useCallback((field: string) => {
    setPendingSortField(field);
  }, []);

  const handleSelectAsc = useCallback(() => {
    setPendingSortDir("asc");
  }, []);

  const handleSelectDesc = useCallback(() => {
    setPendingSortDir("desc");
  }, []);

  const handleApplySort = useCallback(() => {
    setActiveSortField(pendingSortField);
    setActiveSortDir(pendingSortDir);
    setHasExplicitSort(true);
    setIsSortSheetOpen(false);
    onApplied?.(pendingSortField, pendingSortDir);
  }, [pendingSortField, pendingSortDir, onApplied]);

  return {
    activeSortField,
    activeSortDir,
    hasExplicitSort,
    pendingSortField,
    pendingSortDir,
    isSortSheetOpen,
    handleSortPress,
    handleCloseSortSheet,
    handleSelectSort,
    handleSelectAsc,
    handleSelectDesc,
    handleApplySort,
  };
}

export default useDeferredSort;
